import {
  useMediaQuery,
  useTextValidator,
} from '@ace-clublabs/shared-component-library';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { SearchResult } from '@/components/shared/SearchBox';
import type { Destination } from '@/features/destinations/utils/Destination';
import { getDestinationRegionLabel } from '@/features/destinations/utils/DestinationRegionLabelMapper';
import { useDebounce } from '@/hooks/useDebounce';
import useAppointmentStore from '@/store/AppointmentStore';
import useFeatureFlagsStore from '@/store/FeatureFlagsStore';
import { FlowType } from '@/types/FlowType';
import { formatSearchedDestination } from '@/utils/formatSearchedDestination';
import { useRelevanceSearch } from '../api/getRelevanceSearch';
import type { RSSearchBoxProps } from '../components/types';
import type { RelevanceSearchResult } from '../types/RelevanceSearchResult';

const undecidedKeyWords = [
  `don't know`,
  `dont know`,
  'not sure',
  'need help',
  `haven't picked`,
  'havent picked',
];

const mapToSearchResult = (result: RelevanceSearchResult): SearchResult => {
  const isCountry = result.type?.includes('country');
  const isContinent = result.country === 'Unknown';

  return {
    id: result.id,
    title: result.name,
    icon: 'location-dot-duotone',
    subtitle:
      isCountry || isContinent
        ? undefined
        : [result.region, result.country].filter(Boolean).join(', '),
  };
};

export interface UseRSSearchBoxOptions {
  flow: FlowType;
  formVariant?: boolean;
  proceedWithDestination?: (destination: Destination) => void;
}

export const useRSSearchBox = ({
  flow,
  formVariant = false,
  proceedWithDestination,
}: UseRSSearchBoxOptions) => {
  const [search, setSearch] = useState('');
  const [selectedResult, setSelectedResult] =
    useState<RelevanceSearchResult | null>(null);
  const [showWarning, setShowWarning] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [isSearchPanelOpen, setIsSearchPanelOpen] = useState(false);

  const isMobileView = useMediaQuery((theme) => theme.breakpoints.down('md'));

  useEffect(() => {
    if (!isMobileView) {
      setIsSearchPanelOpen(false);
    }
  }, [isMobileView]);

  const useNewDestinations = useFeatureFlagsStore(
    (state) => state.featureFlags.useNewDestinations,
  );

  const setSelectedDestination = useAppointmentStore(
    (state) => state.setSelectedDestination,
  );
  const setSearchedDestination = useAppointmentStore(
    (state) => state.setSearchedDestination,
  );
  const setUserSearch = useAppointmentStore((state) => state.setUserSearch);
  const destinations = useAppointmentStore((state) => state.destinations);

  const debouncedSearch = useDebounce(search, 300);
  const relevanceSearchQuery = useRelevanceSearch(
    debouncedSearch,
    useNewDestinations,
  );

  const undecidedOption: SearchResult = useMemo(
    () => ({
      id: 'undecided',
      title: `I don't know, I haven't picked a destination yet.`,
      icon: 'circle-question-regular',
    }),
    [],
  );

  const validator = useTextValidator({
    label: 'Destination',
    preValidationMutators: [
      ({ value }) => {
        if (searchBoxProps.selectedResult) {
          return searchBoxProps.selectedResult.title;
        }
        return value;
      },
    ],
    validations: [
      ({ value }) => {
        if (!value?.trim()) {
          setShowWarning(true);
          return {
            value: value,
            isValid: false,
            message: 'Please select a destination',
          };
        }
        setShowWarning(false);
        return {
          value: value,
          isValid: true,
        };
      },
    ],
    postValidationMutators: [
      ({ value }) => (searchBoxProps.selectedResult ? '' : value),
    ],
    required: false,
    validationFeedback: 'delayed',
  });

  const searchResults: SearchResult[] | undefined = useMemo(() => {
    const lowerCasedSearch = debouncedSearch.toLowerCase();
    if (
      undecidedKeyWords.some((keyword) => lowerCasedSearch.includes(keyword))
    ) {
      return [undecidedOption];
    }

    if (!relevanceSearchQuery.data) return;
    if (!relevanceSearchQuery.data.length) {
      if (!debouncedSearch) return;
      return [];
    }

    return relevanceSearchQuery.data.map(mapToSearchResult);
  }, [relevanceSearchQuery.data, debouncedSearch, undecidedOption]);

  const selectedSearchResult = useMemo(
    () => (selectedResult ? mapToSearchResult(selectedResult) : null),
    [selectedResult],
  );

  const handleSearchChange = useCallback((searchString: string) => {
    setSearch(searchString);
    setShowWarning(false);
  }, []);

  const handleOtherDestination = useCallback(() => {
    const fallbackName = useNewDestinations ? 'Unknown' : 'Other';
    const unknownDestination = destinations.find(
      (e) => e.name === fallbackName,
    );
    if (!unknownDestination) return;
    setSelectedDestination(unknownDestination);
    proceedWithDestination?.(unknownDestination);
  }, [
    destinations,
    setSelectedDestination,
    useNewDestinations,
    proceedWithDestination,
  ]);

  const handleSelectResult = useCallback(
    (result: SearchResult | null) => {
      if (!result) {
        setSelectedResult(null);
        return;
      }

      if (result.id === undecidedOption.id) {
        handleOtherDestination();
        return;
      }

      const relevanceResult = relevanceSearchQuery.data?.find(
        (e) => e.id === result.id,
      );

      if (!relevanceResult) return;
      setSelectedResult(relevanceResult);
    },
    [relevanceSearchQuery.data, handleOtherDestination, undecidedOption.id],
  );

  const resolveDestination = useCallback((): {
    selectedDestination?: Destination;
    searchedDestination?: string;
    userSearch?: string;
  } => {
    if (!selectedResult) {
      const trimmedSearch = search.trim();
      if (!trimmedSearch) return {};

      const fallbackName = useNewDestinations ? 'Unknown' : 'Other';
      const unknownDestination = destinations.find(
        (e) => e.name === fallbackName,
      );
      return {
        selectedDestination: unknownDestination,
        searchedDestination: '',
        userSearch: trimmedSearch,
      };
    }

    const selectedDestination = destinations.find(
      (dest) => dest.name === selectedResult.aceDestination,
    );

    if (!selectedDestination) return {};

    const regionLabel = getDestinationRegionLabel(
      selectedDestination.name,
      useNewDestinations,
    );

    const mappedDestination: Destination = {
      ...selectedDestination,
      name: regionLabel,
    };

    const searchedDestinationString = formatSearchedDestination([
      selectedResult.name,
      selectedResult.region,
      selectedResult.subRegion,
      selectedResult.country,
    ]);

    return {
      selectedDestination: mappedDestination,
      searchedDestination: searchedDestinationString,
    };
  }, [selectedResult, search, useNewDestinations, destinations]);

  const handleUnmatchedDestination = useCallback(() => {
    const trimmedSearch = search.trim();
    if (!trimmedSearch) return;
    setUserSearch(trimmedSearch);
    setSearchedDestination('');
    handleOtherDestination();
  }, [search, handleOtherDestination, setUserSearch, setSearchedDestination]);

  const handleClickContinue = useCallback(() => {
    if (!selectedResult) {
      if (!search.trim()) {
        setShowWarning(true);
        return;
      }
      setShowWarning(false);
    } else {
      setShowWarning(false);
    }

    const { selectedDestination, searchedDestination, userSearch } =
      resolveDestination();

    if (!selectedDestination) return;

    if (userSearch !== undefined) {
      setUserSearch(userSearch);
    }
    if (searchedDestination !== undefined) {
      setSearchedDestination(searchedDestination);
    }
    setSelectedDestination(selectedDestination);
    proceedWithDestination?.(selectedDestination);
  }, [
    selectedResult,
    search,
    proceedWithDestination,
    setSelectedDestination,
    setSearchedDestination,
    setUserSearch,
    resolveDestination,
  ]);

  const handleSearchPanelOpenChange = useCallback((open: boolean) => {
    setIsSearchPanelOpen(open);
  }, []);

  const handleBlur = useCallback(() => {
    setHasInteracted(true);
    setIsSearchPanelOpen(false);
  }, []);

  const handleFocus = useCallback(() => {
    setIsSearchPanelOpen(true);
  }, []);

  const showWarningState = formVariant ? hasInteracted && showWarning : showWarning;

  const searchBoxProps: RSSearchBoxProps = useMemo(() => {
    const placeholder = showWarningState
      ? 'Please type in a destination.'
      : 'Where would you like to travel?';

    return {
      placeholder,
      showWarning: showWarningState,
      onSelect: handleSelectResult,
      selectedResult: selectedSearchResult,
      value: search,
      onChange: handleSearchChange,
      onBlur: handleBlur,
      searchResults,
      isLoading: relevanceSearchQuery.isLoading,
      onOpenChange: handleSearchPanelOpenChange,
      ...(flow !== FlowType.EMAIL && { onContinueClick: handleClickContinue }),
      validator,
    };
  }, [
    showWarningState,
    handleSelectResult,
    selectedSearchResult,
    search,
    handleSearchChange,
    handleBlur,
    searchResults,
    relevanceSearchQuery.isLoading,
    flow,
    handleClickContinue,
    handleSearchPanelOpenChange,
    validator,
  ]);

  return {
    search,
    selectedResult,
    selectedSearchResult,
    showWarning,
    showWarningState,
    isSearchPanelOpen,
    searchResults,
    isLoading: relevanceSearchQuery.isLoading,
    handleSearchChange,
    handleSelectResult,
    handleSearchPanelOpenChange,
    handleBlur,
    handleFocus,
    handleClickContinue,
    handleUnmatchedDestination,
    searchBoxProps,
    resolveDestination,
  };
};
